import React from 'react';
import BackgroundCover from '../../components/backgroundCover/BackgroundCover';
import { useLanguage } from '../../context/LanguageContext';
import { useEditMode } from '../../context/EditModeContext';
import EditableText from '../../components/EditableText/EditableText';
import { usePageContent } from '../../hooks/usePageContent';

interface PatentLink {
    text: string;
    url: string;
}

interface PatentInfo {
    title: string;
    patentNo?: string;
    description: string | string[];
    links?: PatentLink[];
    images: string[];
}

interface PatentsTranslations {
    pageTitle: string;
    patents: PatentInfo[];
}

function Patents() {
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<PatentsTranslations>('patents');

    // Use translations as fallback content
    const t = content; // Directly use content, loading/error handled below

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    // Handle case where content is not yet loaded or failed to load
    if (!t) {
        return (
            <div className="flex justify-center items-center h-screen">
                {/* Optional: Display an error message or a different placeholder */}
                <p>Content not available.</p>
            </div>
        );
    }

    return (
        <div className="">
            <BackgroundCover
                backgroundHeight={600}
                imagePosition="initial"
                backgroundImage="/images/clockwork_light_background.png" // You might want a different background
            >
                <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-10 hidden md:block">
                    <h1 className="text-4xl font-bold text-white">
                        {isEditMode ? (
                            <EditableText
                                contentKey="patents.pageTitle"
                                defaultValue={t.pageTitle}
                            />
                        ) : t.pageTitle}
                    </h1>
                </div>
            </BackgroundCover>

            <div className="mx-auto px-4 md:px-0 py-12 md:py-24">
                <div className="block md:hidden mb-8">
                    <h1 className="text-4xl font-bold">
                        {isEditMode ? (
                            <EditableText
                                contentKey="patents.pageTitle"
                                defaultValue={t.pageTitle}
                            />
                        ) : t.pageTitle}
                    </h1>
                </div>

                <div className="flex flex-col items-center">
                    <div className="max-w-4xl w-full space-y-16">
                        {t.patents.map((patent, index) => (
                            <section key={index} className="mb-12">
                                <h2 className="text-2xl font-bold mb-4">
                                    {isEditMode ? (
                                        <EditableText
                                            contentKey={`patents.patents.${index}.title`}
                                            defaultValue={patent.title}
                                        />
                                    ) : patent.title}
                                </h2>
                                {patent.patentNo && (
                                    <p className="font-semibold mb-2">
                                        {isEditMode ? (
                                            <EditableText
                                                contentKey={`patents.patents.${index}.patentNo`}
                                                defaultValue={patent.patentNo}
                                            />
                                        ) : patent.patentNo}
                                    </p>
                                )}

                                <div className="text-gray-600 leading-relaxed space-y-4 mb-4">
                                     {isEditMode ? (
                                        Array.isArray(patent.description) ? (
                                            patent.description.map((desc, descIndex) => (
                                                <EditableText
                                                    key={descIndex}
                                                    contentKey={`patents.patents.${index}.description.${descIndex}`}
                                                    defaultValue={desc}
                                                    multiline={true}
                                                />
                                            ))
                                        ) : (
                                            <EditableText
                                                contentKey={`patents.patents.${index}.description`}
                                                defaultValue={patent.description}
                                                multiline={true}
                                            />
                                        )
                                     ) : (
                                        Array.isArray(patent.description) ? (
                                            patent.description.map((desc, descIndex) => (
                                                 <p key={descIndex}>{desc}</p>
                                            ))
                                         ) : (
                                            <p>{patent.description}</p>
                                         )
                                     )}
                                </div>

                                {patent.links && patent.links.length > 0 && (
                                    <div className="mb-4 space-x-2 flex flex-wrap gap-x-2 gap-y-1">
                                        <span className="font-semibold">Published as:</span>
                                        {patent.links.map((link, linkIndex) => (
                                            <a
                                                key={linkIndex}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {isEditMode ? (
                                                    <EditableText
                                                        contentKey={`patents.patents.${index}.links.${linkIndex}.text`}
                                                        defaultValue={link.text}
                                                    />
                                                ) : link.text}
                                            </a>
                                        ))}
                                    </div>
                                )}

                                {patent.images && patent.images.length > 0 && (
                                     <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                                        {patent.images.map((imgSrc, imgIndex) => (
                                            <img
                                                key={imgIndex}
                                                src={imgSrc}
                                                alt={`${patent.title} - Image ${imgIndex + 1}`}
                                                className="w-full h-auto object-contain border rounded"
                                                loading="lazy"
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Patents; 